import { Capacitor } from '@capacitor/core';
import { translate } from './translate';
import { format } from 'date-fns';
import {
  EnvString,
  EquipmentType,
  User,
  GoalType,
  LevelType,
  PlanSlugType,
  Profile,
} from '@sworkit/api';
import { popoverController, PopoverOptions } from '@ionic/core';

export const tosUrl = 'https://m.sworkit.com/TOS.html';
export const privacyPolicyUrl = 'https://m.sworkit.com/privacy.html';

export function removeUndefinedAndNaNRecursive(obj: any) {
  for (const key in obj) {
    if (
      typeof obj[key] === 'undefined' ||
      (typeof obj[key] === 'number' && isNaN(obj[key]))
    ) {
      delete obj[key];
    }
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const newObj = obj[key];
      removeUndefinedAndNaNRecursive(newObj);
    }
  }
  return obj as any;
}

export function injectJS(id: string, src: string) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve('js already injected');
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    const loaded = () => {
      resolve(null);
      script.removeEventListener('load', loaded);
    };
    const error = () => {
      reject('Error loading script.');
      script.removeEventListener('error', error);
    };
    const abort = () => {
      reject('Script loading aborted.');
      script.removeEventListener('abort', abort);
    };
    script.addEventListener('load', loaded);
    script.addEventListener('error', error);
    script.addEventListener('abort', abort);
    document.head.appendChild(script);
  });
}

export function injectCSS(id: string, src: string) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve('css already injected');
      return;
    }
    const link = document.createElement('link');
    link.id = id;
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('href', src);
    link.addEventListener('load', resolve);
    link.addEventListener('error', () => reject('Error loading css.'));
    link.addEventListener('abort', () => reject('CSS loading aborted.'));
    document.head.appendChild(link);
  });
}

export function getDateTimeAndTimezoneOffset(date: Date = new Date()) {
  return getDateTime(date) + getTimezoneOffset(date);
}

function getTimezoneOffset(date: Date) {
  var timezone_offset_min: number = date.getTimezoneOffset();
  var offset_hrs: string | number = Math.abs(timezone_offset_min / 60);
  var offset_min: string | number = Math.abs(timezone_offset_min % 60);
  var timezone_standard: string;

  if (offset_hrs < 10) offset_hrs = '0' + offset_hrs;

  if (offset_min < 10) offset_min = '0' + offset_min;

  // Add an opposite sign to the offset
  // If offset is 0, it means timezone is UTC
  if (timezone_offset_min < 0)
    timezone_standard = '+' + offset_hrs + ':' + offset_min;
  else if (timezone_offset_min > 0)
    timezone_standard = '-' + offset_hrs + ':' + offset_min;
  else if (timezone_offset_min == 0) timezone_standard = 'Z';

  // Timezone difference in hours and minutes
  // String such as +5:30 or -6:00 or Z
  return timezone_standard;
}

function getDateTime(dt: Date) {
  let current_date: string | number = dt.getDate();
  let current_month: string | number = dt.getMonth() + 1;
  let current_year: string | number = dt.getFullYear();
  let current_hrs: string | number = dt.getHours();
  let current_mins: string | number = dt.getMinutes();
  let current_secs: string | number = dt.getSeconds();
  let current_datetime: string;

  // Add 0 before date, month, hrs, mins or secs if they are less than 0
  current_date = current_date < 10 ? '0' + current_date : current_date;
  current_month = current_month < 10 ? '0' + current_month : current_month;
  current_hrs = current_hrs < 10 ? '0' + current_hrs : current_hrs;
  current_mins = current_mins < 10 ? '0' + current_mins : current_mins;
  current_secs = current_secs < 10 ? '0' + current_secs : current_secs;

  // Current datetime
  // String such as 2016-07-16T19:20:30
  current_datetime =
    current_year +
    '-' +
    current_month +
    '-' +
    current_date +
    'T' +
    current_hrs +
    ':' +
    current_mins +
    ':' +
    current_secs;
  return current_datetime;
}

export const betaWebAppURL = 'https://beta.sworkit.com';
export const prodWebAppURL = 'https://app.sworkit.com';

export function isOnDev() {
  return location.href.includes('localhost:');
}

export function isOnBetaWebApp() {
  return location.href.includes(betaWebAppURL);
}

export function isOnProdWebApp() {
  const onProdWebApp = location.href.includes(prodWebAppURL);
  return onProdWebApp;
}

export function getTodayString() {
  const now = new Date();
  return format(now, 'yyyy-MM-dd');
}

export function getSwDateString(date: Date) {
  return format(date, 'yyyy-MM-dd');
}

export function convertDistances(meters: number) {
  return {
    miles: roundToOneDecimal((meters || 0) / 1609.344),
    kilometers: roundToOneDecimal((meters || 0) / 1000),
  };
}

export function getConvertedDistanceForUnit(
  meters: number,
  distanceUnit: Profile['distanceUnit'],
) {
  const key = distanceUnit === 'kilometers' ? distanceUnit : 'miles';
  return (
    {
      miles: roundToOneDecimal((meters || 0) / 1609.344),
      kilometers: roundToOneDecimal((meters || 0) / 1000),
    }[key] || 0
  );
}

export function getDistanceUnitTranslationKey(
  distanceUnit: Profile['distanceUnit'],
) {
  const key = distanceUnit === 'kilometers' ? 'KILOMETERS' : 'MILES';
  return key;
}

export function forceUpdateVirtualScroll() {
  // hack for fixing virtual scroll not re-rendering
  // https://github.com/ionic-team/ionic/issues/14681#issuecomment-403000988
  // also referenced here: https://github.com/ionic-team/ionic/issues/16632
  const event = new window.Event('resize');
  window.dispatchEvent(event);
}

export function getConnectedApps(state: SworkitState) {
  // @todo rewrite to be an array vs a string?
  const connected_apps = [];
  if (
    state.user.integrations.myFitnessPal &&
    state.user.integrations.myFitnessPal.accessToken
  ) {
    connected_apps.push('mfp');
  }
  if (
    state.user.integrations.strava &&
    state.user.integrations.strava.accessToken
  ) {
    connected_apps.push('strava');
  }
  if (state.user.integrations?.fitbit?.access_token) {
    connected_apps.push('fitbit');
  }
  if (state.health.healthWriteEnabled && state.health.healthWriteAuthorized) {
    const platform = getHealthLogType();
    if (platform) {
      connected_apps.push(platform);
    }
  }
  return connected_apps.join(', ');
}

export function getHealthLogType() {
  const platform = Capacitor.getPlatform();
  switch (platform) {
    case 'ios':
      return 'apple-health';
    case 'android':
      return 'health-connect';
    default:
      return null;
  }
}
export function getMonthShortNames() {
  const translatedNames = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ].map((key: TranslateKey) => {
    return translate(key);
  });
  return translatedNames;
}

export function getEnvFromState(state: SworkitPersistedState) {
  let env: EnvString = 'prod';
  if (state && state.user && state.user.account && state.user.account.env) {
    env = state.user.account.env;
  }
  return env;
}

export function waitFor(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

export function getNumberArray(min: number, max: number) {
  const numberArray: number[] = [];
  for (var i = min; i <= max; i++) {
    numberArray.push(i);
  }
  return numberArray;
}

export function getUrlParameter(name: string, decode?: boolean) {
  // https://davidwalsh.name/query-string-javascript
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  var results = regex.exec(location.search);
  return results === null
    ? ''
    : decode
    ? decodeURIComponent(results[1])
    : results[1];
}

export function getParentURL() {
  var url =
    window.location != window.parent.location
      ? document.referrer
      : document.location.href;
  return url;
}

export function getTextColor() {
  return getComputedStyle(document.body)
    .getPropertyValue('--ion-text-color')
    .replace(' ', '');
}

export function getDateAtMidnightFromString(dateString: string) {
  if (!dateString) return null;
  const dateArray = dateString.split(/\D/);
  return new Date(
    Number(dateArray[0]),
    Number(dateArray[1]) - 1,
    Number(dateArray[2]),
  );
}

export function getDateAtTheEndOfTheDayFromString(dateString: string) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split(/\D/);
  return new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59);
}

export function getGoalFromSlug(slug: string) {
  if (!slug) return null;
  if (typeof slug !== 'string') return null;
  return slug.split('-')[0] as GoalType;
}

export function getLevelFromSlug(slug: string) {
  if (!slug) return null;
  return slug.split('-')[1] as LevelType;
}

export function getPlanTabUrl(activePlanSlug: PlanSlugType) {
  if (activePlanSlug) {
    return `/plans/${activePlanSlug}`;
  } else {
    return '/goals';
  }
}

export const equipmentIconSrc: { [key in EquipmentType]: string } = {
  kettlebell: '/assets/icons/kettlebells.svg',
  bodyweight: '/assets/icons/bodyweight.svg',
  dumbbell: '/assets/icons/dumbbell.svg',
  band: '/assets/icons/band.svg',
  loop: '/assets/icons/loop.svg',
  foamroller: '/assets/icons/foamroller.svg',
  barre: '/assets/icons/barre.svg',
  chair: '/assets/icons/chair.svg',
  pilatesball: '/assets/icons/pilatesball.svg',
};

export function getChallengeIdsFromUser(user: User) {
  return Object.keys(user?.challenges || {}).map((challengeId) => challengeId);
}

export function exhaustiveCheck(value: never) {
  // this is used to ensure all switch cases return a value.
  // if we add a new value type that the switch should account for, typescript will tell us where the case needs to be added
  return value;
}

export function roundToOneDecimal(num: number): number {
  return Math.round(num * 10) / 10;
}

export function shuffle<T>(array: T[]) {
  let currentIndex = array.length;
  let temporaryValue: T;
  let randomIndex: number;
  // While there remain elements to shuffle...
  while (0 !== currentIndex) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;
    // And swap it with the current element.
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }
  return array;
}

export function arrayEquals(a, b) {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
}
export function hasContent(htmlString: string) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  return tempDiv.textContent?.trim()?.length > 0;
}

export const deepEqual = (objA, objB, map = new WeakMap()) => {
  // P1
  if (Object.is(objA, objB)) return true;

  // P2
  if (objA instanceof Date && objB instanceof Date) {
    return objA.getTime() === objB.getTime();
  }
  if (objA instanceof RegExp && objB instanceof RegExp) {
    return objA.toString() === objB.toString();
  }

  // P3
  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  // P4
  if (map.get(objA) === objB) return true;
  map.set(objA, objB);

  // P5
  const keysA = Reflect.ownKeys(objA);
  const keysB = Reflect.ownKeys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (let i = 0; i < keysA.length; i++) {
    if (
      !Reflect.has(objB, keysA[i]) ||
      !deepEqual(objA[keysA[i]], objB[keysA[i]], map)
    ) {
      return false;
    }
  }

  return true;
};

export async function displayDatePicker(
  date: string | number | Date,
  callback: (newDate: Date) => void,
) {
  let dateString: string;

  switch (typeof date) {
    case 'string':
      const timestamp = Date.parse(date);
      dateString = isNaN(timestamp)
        ? new Date().toISOString()
        : (dateString = new Date(timestamp).toISOString());
      break;
    case 'number':
      dateString = new Date(date).toISOString();
      break;
    case 'undefined':
      dateString = new Date().toISOString();
      break;
    default:
      dateString = date.toISOString();
      break;
  }

  const popoverOptions: PopoverOptions = {
    component: 'sw-date',
    cssClass: 'sw-date',
    backdropDismiss: false,
    alignment: 'center',
    componentProps: {
      popoverController,
      dateString,
    },
  };
  const popover = await popoverController.create(popoverOptions);
  popover.present();
  popover.onDidDismiss().then((item: { data: string; role: any }) => {
    callback(item.data ? new Date(item.data) : new Date(dateString));
  });
}

export const debounce = (func: { (): any; apply?: any }, ms = 0) => {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), ms);
  };
};

export function ionLines(
  arr: any[] | undefined,
  index: number,
  lastItem: HTMLIonItemElement['lines'] = 'full',
): HTMLIonItemElement['lines'] {
  return index + 1 === arr?.length ? lastItem : undefined;
}
